import type { CSSProperties, JSX } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import frameUrl from '../assets/avatar-frame.png';
import { useVoiceFloat } from '../../voice/voiceFloat.js';
import './AvatarFrame.css';

/**
 * Structural minimum of a seated player. Any phase's view-mapped player type
 * satisfies this if it carries id + name + avatar metadata; the frame doesn't
 * care about role, alive, or any phase-specific field. Phases extend this with
 * whatever they additionally need (lobby's LobbyPlayer is a structural superset).
 */
export interface SeatPlayer {
  id: string;
  name: string;
  avatarUrl: string;
  isDefaultAvatar: boolean;
  defaultAvatarIndex?: 0 | 1 | 2 | 3 | 4 | 5;
}

interface AvatarFrameProps {
  player: SeatPlayer;
  isHost: boolean;
  /**
   * Ghost POV only: when `true`, renders an italic-small-caps `Fellow`
   * inscription in `--susdle-ghost-ink` (the Spectral Silver-Blue text tier,
   * cool-hue cousin of `--susdle-ink-soft`) inside the name plate, above the
   * name, flanked by 7×1px hairlines — same compositional slot as the
   * host-mark. Kept separate from the voted-state border (which lives on
   * the surrounding cast cell, not the tile) so the two signals never
   * collapse. `state.ghostIds` is GHOSTS-tagged server-side, so the marker
   * can't reach a non-Ghost client even if a parent passes `true` by mistake.
   */
  isFellowGhost?: boolean;
  /**
   * When `true`, the player is disconnected-but-alive (`Player.connected ===
   * false`). Renders the away treatment: frame ink drops to ash, the portrait
   * recedes behind a paper veil with a slow gutter, and an italic-small-caps
   * "Away" inscription sits in the name-zone (the Host/Fellow slot). The name
   * stays full ink so the player is still recognizable, and the cell stays a
   * valid vote target. Holds both accents out — away is neither a consequence
   * (they may reconnect) nor the Ghost role. Default `false`.
   */
  away?: boolean;
  /**
   * When `false`, renders as a plain `<div>` without framer-motion's
   * `layout` tracking or the 700ms y-slide entry beat. Used by FlipCastGrid
   * which puts AvatarFrame on the back face of a 3D-rotated card —
   * framer-motion's `getBoundingClientRect`-based layout tracker reacts to
   * the parent's rotation and fights it, leaving the AvatarFrame painted in
   * the wrong place or with the wrong opacity for the camera angle. Plain
   * div has no such side-effect; it just rides the parent's transform.
   *
   * Default `true` preserves the lobby's company-grid join/leave
   * choreography (where layout tracking IS what makes new joins land
   * gracefully into reflowed slots).
   */
  motionEnabled?: boolean;
  /**
   * When `true`, the framer-motion entry beat is skipped (mounts at rest,
   * no y-slide). Used by the Ghost-meeting cast on a cold arrival. Framer
   * `initial` is consumed only at mount, so toggling later is a no-op.
   * Default `false`.
   */
  suppressEntry?: boolean;
  /**
   * When `true`, the voice-float never attaches to this tile.
   * Distinct from `motionEnabled` (which is about framer-motion LAYOUT
   * tracking): the V/F clue-giving cast keeps its tiles on flipped
   * MysteryFlipGrid card faces with motionEnabled=false for the whole
   * phase, and those tiles must still float once their card has settled.
   * Used by the RevealPlate (the float never plays on the reveal arc) and
   * the page-turn snapshot content (cinematic suppression window).
   * Default `false`.
   */
  suppressFloat?: boolean;
}

/* Passed inline so a single PNG import threads through to every seat's CSS
   without each component needing to import the URL itself. */
const FRAME_STYLE = { '--susdle-frame-art': `url(${frameUrl})` } as CSSProperties;

/* Stable FNV-1a 32-bit hash of a seed string mapped to a deterministic
   background-position offset for the paper-grain noise field. A uniform
   grain across eight seats reads as a global filter pass; a per-seat
   shift makes each card a different patch of the same paper stock. */
function paperGrainShift(seed: string): string {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const x = (h >>> 0) % 150;
  const y = (h >>> 8) % 150;
  return `-${x}px -${y}px`;
}

/**
 * A framed Discord avatar with an inscribed name-plate inside the lower third
 * of the frame. Per DESIGN.md: the asset is a public-domain 1878 woodcut
 * frame, cleaned and recolored at render time via a CSS mask so the ink
 * inherits `--susdle-ink` and the cream background stays transparent.
 * Avatar + name plate compose as one tile (carte-de-visite ancestry); there
 * is no separate name plate beneath the frame.
 *
 * Host-removes-guest was retired in the two-page-spread refactor: Discord
 * Activity's room moderation handles mid-lobby removals at the platform
 * level, so the portrait surface no longer carries a kick affordance.
 */
export function AvatarFrame({
  player,
  isHost,
  isFellowGhost,
  away = false,
  motionEnabled = true,
  suppressEntry = false,
  suppressFloat = false,
}: AvatarFrameProps): JSX.Element {
  const { t } = useTranslation();
  /* Gate the join/leave choreography on prefers-reduced-motion. The
     CSS-side transitions are RM-zeroed through `--susdle-dur-*` tokens
     in tokens.css, but Framer Motion's `transition` does not consume
     those tokens, so without this hook a reduced-motion user still
     gets the 700ms y-slide entry. */
  const reduceMotion = useReducedMotion();

  const tone =
    player.isDefaultAvatar && player.defaultAvatarIndex !== undefined
      ? `var(--susdle-seat-tone-${player.defaultAvatarIndex})`
      : null;

  const seatLabel = isHost
    ? t('lobby.seat.labelHost', { name: player.name })
    : away
      ? t('presence.away.seatLabel', { name: player.name })
      : player.name;

  const grainStyle = {
    '--susdle-grain-shift': paperGrainShift(player.id),
  } as CSSProperties;

  /* Voice float (voice activity, voice/voiceFloat.ts). Inert until a
     SpeakingProvider is mounted above. Eligibility is the explicit
     `suppressFloat` prop — deliberately NOT motionEnabled, which the V/F
     clue-giving cast disables for the whole phase (flipped card faces)
     while its tiles must still float. Mid-choreography suppression on
     those cards is CSS (`--float-damp: 0` on unrevealed cells,
     MysteryFlipGrid.css). */
  const floatRef = useVoiceFloat(player.id, !suppressFloat);

  /* Tile content is identical between the motion and static branches —
     extracted so the only difference is the outer wrapper element. */
  const tile = (
    <div
      ref={floatRef}
      className="susdle-seat__tile"
      style={FRAME_STYLE}
      data-host={isHost ? 'yes' : 'no'}
    >
      <div className="susdle-seat__content">
        <div
          className="susdle-seat__avatar-zone"
          style={tone ? { backgroundColor: tone } : undefined}
        >
          {player.isDefaultAvatar ? (
            <AnonymousFigureSigil />
          ) : (
            <img
              className="susdle-seat__avatar"
              src={player.avatarUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
          )}
          <span aria-hidden className="susdle-seat__paper-grain" style={grainStyle} />
        </div>

        <div className="susdle-seat__name-zone">
          {isHost ? (
            <span aria-hidden className="susdle-seat__host-mark">
              {t('lobby.host.indicator')}
            </span>
          ) : null}
          {away && !isHost ? (
            <span aria-hidden className="susdle-seat__away-mark">
              {t('presence.away.indicator')}
            </span>
          ) : null}
          {isFellowGhost && !isHost && !away ? (
            <span aria-hidden className="susdle-seat__fellow-mark">
              {t('meeting.fellow.indicator')}
            </span>
          ) : null}
          <span className="susdle-seat__name" title={player.name}>{player.name}</span>
        </div>
      </div>

      <span aria-hidden className="susdle-seat__frame-art" />
    </div>
  );

  /* Static branch: plain div, no framer-motion. Used by FlipCastGrid where
     the AvatarFrame sits on the back face of a 3D-rotated card and any
     layout tracking would fight the rotation. */
  if (!motionEnabled) {
    return (
      <div
        className="susdle-seat"
        data-default-avatar={player.isDefaultAvatar ? 'yes' : 'no'}
        data-away={away ? 'yes' : 'no'}
        aria-label={seatLabel}
      >
        {tile}
      </div>
    );
  }

  return (
    <motion.div
      className="susdle-seat"
      data-default-avatar={player.isDefaultAvatar ? 'yes' : 'no'}
      data-away={away ? 'yes' : 'no'}
      aria-label={seatLabel}
      layout
      initial={suppressEntry ? false : reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
      animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { duration: 0.7, ease: [0.25, 1, 0.5, 1] }
      }
    >
      {tile}
    </motion.div>
  );
}

/**
 * Mystery placeholder: same tile geometry as AvatarFrame, but the avatar zone
 * shows an italic `?` on the player's would-be seat-tone, and the name plate
 * shows a single middle-dot inscription. Used by V/F POV in the Ghost-meeting
 * mystery grid: the row of cells shuffles on a 1.8s interval, conveying "the
 * playing order is being decided" without leaking which Ghost voted for whom.
 *
 * The placeholder deliberately keeps the seat-tone in color — the duotone
 * filter pass that AvatarFrame applies to real photos would wash the tones
 * to muddy gray here, defeating the shuffle's whole point. Each cell takes a
 * different tone so swaps register as a visible rearrangement.
 */
export function MysteryFrame({
  tone,
  seed,
}: {
  tone: 0 | 1 | 2 | 3 | 4 | 5;
  seed?: string;
}): JSX.Element {
  const grainStyle = {
    '--susdle-grain-shift': paperGrainShift(seed ?? `mystery-${tone}`),
  } as CSSProperties;
  return (
    <div className="susdle-seat susdle-seat--mystery" aria-hidden>
      <div className="susdle-seat__tile" style={FRAME_STYLE}>
        <div className="susdle-seat__content">
          <div
            className="susdle-seat__avatar-zone susdle-seat__avatar-zone--mystery"
            style={{ backgroundColor: `var(--susdle-seat-tone-${tone})` }}
          >
            <span className="susdle-seat__mystery-glyph">?</span>
            <span aria-hidden className="susdle-seat__paper-grain" style={grainStyle} />
          </div>
          <div className="susdle-seat__name-zone susdle-seat__name-zone--mystery">
            <span className="susdle-seat__mystery-dot">·</span>
          </div>
        </div>
        <span aria-hidden className="susdle-seat__frame-art" />
      </div>
    </div>
  );
}

/** A blank seat: same tile geometry, frame ink dimmed to ash, a faint
 *  woodcut chair waiting to be filled, blank name plate. */
export function EmptyChair({ index }: { index: number }): JSX.Element {
  const grainStyle = {
    '--susdle-grain-shift': paperGrainShift(`empty-${index}`),
  } as CSSProperties;
  return (
    <div
      className="susdle-seat susdle-seat--empty"
      aria-hidden
      data-seat-index={index}
    >
      <div className="susdle-seat__tile" style={FRAME_STYLE}>
        <div className="susdle-seat__content">
          <div className="susdle-seat__avatar-zone susdle-seat__avatar-zone--empty">
            <WoodenChairSigil />
            <span aria-hidden className="susdle-seat__paper-grain" style={grainStyle} />
          </div>
          <div className="susdle-seat__name-zone susdle-seat__name-zone--empty">
            <span className="susdle-seat__name" />
          </div>
        </div>
        <span aria-hidden className="susdle-seat__frame-art susdle-seat__frame-art--dim" />
      </div>
    </div>
  );
}

/**
 * Default-avatar substitute. An outline-only Victorian bust seen head-on:
 * a clear oval head where the portrait would be, a high collar, narrow
 * shoulders, faint horizontal hatching behind. The reference is a Victorian
 * album silhouette portrait card before a photograph has been mounted, not
 * a hooded figure. Real Discord avatars (post-SDK) replace this entirely.
 */
export function AnonymousFigureSigil(): JSX.Element {
  return (
    <svg viewBox="0 0 76 76" aria-hidden focusable={false} className="susdle-seat__sigil">
      <g
        stroke="currentColor"
        strokeWidth="0.5"
        opacity="0.18"
        strokeLinecap="round"
      >
        <line x1="6" y1="14" x2="70" y2="14" />
        <line x1="6" y1="22" x2="70" y2="22" />
        <line x1="6" y1="30" x2="70" y2="30" />
        <line x1="6" y1="38" x2="70" y2="38" />
        <line x1="6" y1="46" x2="70" y2="46" />
        <line x1="6" y1="54" x2="70" y2="54" />
        <line x1="6" y1="62" x2="70" y2="62" />
      </g>
      <path
        d="M 12 76 Q 12 64 16 56 Q 22 50 28 48 Q 30 43 31.5 37 C 28 30 28 18 38 18 C 48 18 48 30 44.5 37 Q 46 43 48 48 Q 54 50 60 56 Q 64 64 64 76 Z"
        fill="var(--page-plate, var(--susdle-paper-bright))"
        stroke="currentColor"
        strokeWidth={1.25}
        strokeLinejoin="round"
      />
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.1}
        opacity="0.55"
      >
        <path d="M 28 48 Q 38 53 48 48" />
        <path d="M 35 50 L 38 55 L 41 50" strokeWidth="0.9" />
      </g>
    </svg>
  );
}

/** Faint woodcut parlour chair, seen from the front. Sits inside the empty
 *  seat's avatar zone at low opacity to read as "an empty chair waiting." */
function WoodenChairSigil(): JSX.Element {
  return (
    <svg viewBox="0 0 76 76" aria-hidden focusable={false} className="susdle-seat__chair-sigil">
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.15}
      >
        <path d="M 22 14 Q 38 11 54 14" />
        <path d="M 23 14 L 24 44" />
        <path d="M 53 14 L 52 44" />
        <path d="M 30 16 L 30 44" opacity="0.7" />
        <path d="M 35 15 L 35 44" opacity="0.7" />
        <path d="M 41 15 L 41 44" opacity="0.7" />
        <path d="M 46 16 L 46 44" opacity="0.7" />
        <path d="M 19 44 L 57 44 Q 58 47 56 49 L 20 49 Q 18 47 19 44 Z" />
        <path d="M 22 49 L 23 65" />
        <path d="M 54 49 L 53 65" />
        <path d="M 27 49 L 28 62" />
        <path d="M 49 49 L 48 62" />
        <path d="M 23 58 L 53 58" />
        <path d="M 14 67 L 62 67" opacity="0.3" strokeWidth="0.9" />
      </g>
    </svg>
  );
}
